import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';

// Stripe caps a tax rate's display_name at 50 characters; exceeding it makes
// stripe.taxRates.create/update throw, so we reject over-long names up front.
export const TAX_RATE_DISPLAY_NAME_MAX_LENGTH = 50;

export const TaxRateResponseValidationSchema = z
  .object({
    id: ObjectIdStringValidationSchema.meta({ description: 'Unique identifier for the tax rate.' }),
    sellerId: z
      .string()
      .meta({ description: 'Unique identifier for the seller that owns this tax rate.' }),
    stripeId: z.string().meta({
      description: "The corresponding Stripe Tax Rate ID on the merchant's Stripe account.",
    }),
    displayName: z.string().meta({
      description: 'Short name describing the type of tax, for example VAT, GST, or Sales Tax.',
      example: 'VAT',
    }),
    inclusive: z.boolean().meta({
      description: 'Whether the tax is inclusive (`true`) or exclusive (`false`).',
      example: false,
    }),
    percentage: z.number().min(0).max(100).meta({
      description: 'Tax rate percentage out of 100.',
      example: 20,
    }),
    default: z.boolean().meta({
      description: 'Whether this is the default tax rate for the seller.',
      example: false,
    }),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'TaxRate' });

export const CreateTaxRateInputValidationSchema = z.object({
  displayName: z.string().trim().min(1).max(TAX_RATE_DISPLAY_NAME_MAX_LENGTH).meta({
    description:
      'Short name describing the type of tax, for example VAT, GST, or Sales Tax. Displayed in the checkout and invoices. Maximum 50 characters.',
    example: 'VAT',
  }),
  inclusive: z.boolean().meta({
    description:
      'Whether the tax is inclusive (`true`, meaning it is already included in the price) or exclusive (`false`, meaning it is added on top of the price).',
    example: false,
  }),
  percentage: z
    .number()
    .min(0)
    .max(100)
    .refine(value => Number(value.toFixed(2)) === value, {
      message: 'Percentage must have at most 2 decimal places',
    })
    .meta({
      description: 'Tax rate percentage out of 100. Maximum 2 decimal places.',
      example: 20,
    }),
  default: z.boolean().optional().meta({
    description:
      'Whether this should be the default tax rate. When `true`, this rate is pre-selected for pages using fixed tax mode and no explicit `fixedTaxRateIds`.',
    example: false,
  }),
});

export const CreateTaxRateResponseValidationSchema = z.object({
  data: TaxRateResponseValidationSchema,
});

export const ListTaxRatesResponseValidationSchema = z.object({
  data: z.array(TaxRateResponseValidationSchema),
});

export const HistoricalTaxRateSnapshotValidationSchema = z
  .object({
    taxRate: ObjectIdStringValidationSchema.meta({
      description: 'Tax rate ID this snapshot was taken from.',
    }),
    stripeId: z.string().meta({
      description: 'The ID of the tax rate in Stripe at the time of the transaction.',
    }),
    displayName: z.string().meta({
      description: 'The name of the tax rate. Used in the checkout and invoice.',
      example: 'VAT',
    }),
    default: z.boolean().optional(),
    active: z.boolean().optional(),
    inclusive: z.boolean().meta({ description: 'Whether the tax rate is inclusive.' }),
    percentage: z.number().min(0).max(100).meta({ description: 'The percentage of the tax rate.' }),
    createdAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime().optional(),
  })
  .meta({
    id: 'HistoricalTaxRateSnapshot',
    description: 'Tax rate values captured at the time of the transaction.',
  });

export const TaxSourceValidationSchema = z.enum(['fixed_tax_rate', 'stripe_tax']);

export const TAX_SOURCE_FIELD_DESCRIPTION =
  'How tax was applied. `fixed_tax_rate` when tax rates were applied directly to the record. `stripe_tax` when tax was calculated by Stripe based on the transaction details and Stripe Tax settings.';

export const TAX_RATES_FIELD_DESCRIPTION =
  'Fixed tax rates applied to this record. Populated when `taxSource` is `fixed_tax_rate`.';
